import React, { useState, useEffect, Suspense } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useItems } from '../../contexts/ItemsContext';
import { useSettings } from '../../hooks/useSettings';
import UnifiedItemList from '../Items/UnifiedItemList';
import ItemTypeModal from '../Items/ItemTypeModal';
import PasswordForm from '../Password/PasswordForm';
import CreditCardForm from '../CreditCard/CreditCardForm';
import ApiKeyForm from '../ApiKey/ApiKeyForm';
import PasswordShareModal from '../Password/PasswordShareModal';
import SearchBar from '../Password/SearchBar';
import FilterPanel from '../Items/FilterPanel';
import DashboardMobile from './DashboardMobile';
import { SearchBox, ActionButton, FilterLayout, Button, Modal } from '../UI';
import {
  SecurityRecommendations,
  SettingsModal,
  ShareHistoryModal,
  LoadingFallback,
  ErrorFallback
} from '../LazyComponents.jsx';
import { Plus, RefreshCw, RotateCcw } from 'lucide-react';
import './Dashboard.css';

/**
 * 통합 대시보드 컴포넌트
 * 패스워드, 신용카드, API 키를 통합 관리
 */
function DashboardNew({ onMenuAction }) {
  // All hooks must be called before any conditional returns
  const { user, logout } = useAuth();
  const { settings } = useSettings();
  const { 
    allItems,
    stats,
    loading, 
    error, 
    filters,
    loadItems,
    searchItems,
    addItem,
    updateItem,
    deleteItem,
    updateFilters,
    resetFilters
  } = useItems();
  
  // 모달 상태
  const [showItemTypeModal, setShowItemTypeModal] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formType, setFormType] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [sharingPassword, setSharingPassword] = useState(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showSecurityRecommendations, setShowSecurityRecommendations] = useState(false);
  
  // Check if mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 아이템 타입 선택
  const handleSelectItemType = (type) => {
    setFormType(type);
    setShowForm(true);
    setEditingItem(null);
  };

  // 아이템 추가/수정
  const handleSubmitItem = async (itemData) => {
    setFormLoading(true);
    try {
      if (editingItem) {
        await updateItem(editingItem.itemType, editingItem.id, itemData);
      } else {
        await addItem(formType, itemData);
      }
      setShowForm(false);
      setEditingItem(null);
      setFormType(null);
    } catch (error) {
      console.error('아이템 저장 실패:', error);
    } finally {
      setFormLoading(false);
    }
  };

  // 아이템 수정
  const handleEditItem = (itemType, item) => {
    setEditingItem(item);
    setFormType(itemType);
    setShowForm(true);
  };

  // 아이템 삭제
  const handleDeleteItem = async (itemType, itemId) => {
    if (window.confirm('정말 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) {
      try {
        await deleteItem(itemType, itemId);
      } catch (error) {
        console.error('아이템 삭제 실패:', error);
      }
    }
  };

  // 즐겨찾기 토글
  const handleToggleFavorite = async (itemType, itemId, isFavorite) => {
    try {
      await updateItem(itemType, itemId, { isFavorite });
    } catch (error) {
      console.error('즐겨찾기 업데이트 실패:', error);
    }
  };

  // 패스워드 공유
  const handleSharePassword = (password) => {
    setSharingPassword(password);
    setShowShareModal(true);
  };

  // 검색
  const handleSearch = (searchTerm) => {
    updateFilters({ searchTerm });
  };

  // 필터 변경
  const handleFilterChange = (filterKey, value) => {
    updateFilters({ [filterKey]: value });
  };

  // 사이드바 메뉴 액션 처리
  useEffect(() => {
    if (onMenuAction) {
      const handleMenuAction = (menuId) => {
        switch (menuId) {
          case 'passwords':
            // 모든 항목 표시 (필터 초기화)
            resetFilters();
            break;
          default:
            // 다른 메뉴는 상위 컴포넌트에서 처리
            break;
        }
      };
      
      // 전역 이벤트 리스너로 메뉴 액션 수신
      window.dashboardMenuAction = handleMenuAction;
      
      return () => {
        delete window.dashboardMenuAction;
      };
    }
  }, [resetFilters, onMenuAction]);

  // 폼 컴포넌트 렌더링
  const renderForm = () => {
    if (!showForm || !formType) return null;

    const commonProps = {
      onSubmit: handleSubmitItem,
      onCancel: () => {
        setShowForm(false);
        setEditingItem(null);
        setFormType(null);
      },
      loading: formLoading
    };

    switch (formType) {
      case 'password':
        return (
          <Modal 
            isOpen={showForm} 
            onClose={commonProps.onCancel}
            size="lg"
            showCloseButton={false}
            className="form-modal"
          >
            <PasswordForm 
              {...commonProps} 
              editingPassword={editingItem} 
              key={editingItem ? editingItem.id : 'new'} 
            />
          </Modal>
        );
      case 'creditcard':
        return (
          <Modal 
            isOpen={showForm} 
            onClose={commonProps.onCancel}
            size="lg"
            showCloseButton={false}
            className="form-modal"
          >
            <CreditCardForm 
              {...commonProps} 
              editingCard={editingItem} 
              key={editingItem ? editingItem.id : 'new'} 
            />
          </Modal>
        );
      case 'apikey':
        return (
          <Modal 
            isOpen={showForm} 
            onClose={commonProps.onCancel}
            size="lg"
            showCloseButton={false}
            className="form-modal"
          >
            <ApiKeyForm 
              {...commonProps} 
              editingKey={editingItem} 
              key={editingItem ? editingItem.id : 'new'} 
            />
          </Modal>
        );
      default:
        return null;
    }
  };

  // Render mobile version if on mobile device
  if (isMobile) {
    return <DashboardMobile onMenuAction={onMenuAction} />;
  }

  return (
    <div className="dashboard">

      {/* 검색 및 필터 통합 */}
      <FilterLayout
        searchSlot={
          <SearchBox
            value={filters.searchTerm}
            onChange={handleSearch}
            placeholder="제목, 사용자명, 웹사이트, API 키 이름, 제공자로 검색..."
          />
        }
        filterSlot={
          <>
            <div className="filter-checkboxes">
              <label className="filter-checkbox">
                <input
                  type="checkbox"
                  checked={filters.types.includes('password')}
                  onChange={(e) => {
                    const newTypes = e.target.checked
                      ? [...filters.types, 'password']
                      : filters.types.filter(t => t !== 'password');
                    handleFilterChange('types', newTypes);
                  }}
                />
                <span>패스워드</span>
              </label>
              <label className="filter-checkbox">
                <input
                  type="checkbox"
                  checked={filters.types.includes('creditcard')}
                  onChange={(e) => {
                    const newTypes = e.target.checked
                      ? [...filters.types, 'creditcard']
                      : filters.types.filter(t => t !== 'creditcard');
                    handleFilterChange('types', newTypes);
                  }}
                />
                <span>신용카드</span>
              </label>
              <label className="filter-checkbox">
                <input
                  type="checkbox"
                  checked={filters.types.includes('apikey')}
                  onChange={(e) => {
                    const newTypes = e.target.checked
                      ? [...filters.types, 'apikey']
                      : filters.types.filter(t => t !== 'apikey');
                    handleFilterChange('types', newTypes);
                  }}
                />
                <span>API키</span>
              </label>
            </div>

            <select
              className="filter-select"
              value={filters.favoritesOnly ? 'favorites' : (filters.category || '')}
              onChange={(e) => {
                if (e.target.value === 'favorites') {
                  handleFilterChange('favoritesOnly', true);
                  handleFilterChange('category', '');
                } else {
                  handleFilterChange('favoritesOnly', false);
                  handleFilterChange('category', e.target.value);
                }
              }}
            >
              <option value="">전체</option>
              <option value="favorites">★ 즐겨찾기</option>
              <option value="website">웹사이트</option>
              <option value="email">이메일</option>
              <option value="entertainment">엔터테인먼트</option>
              <option value="finance">금융</option>
              <option value="development">개발</option>
              <option value="other">기타</option>
            </select>

            <select
              className="filter-select"
              value={filters.sortBy || 'updatedAt'}
              onChange={(e) => handleFilterChange('sortBy', e.target.value)}
            >
              <option value="updatedAt">최근 수정순</option>
              <option value="createdAt">생성일순</option>
              <option value="title">이름순</option>
              <option value="usage">사용 빈도순</option>
            </select>

            <ActionButton
              variant="ghost"
              size="icon-only"
              icon={RotateCcw}
              onClick={resetFilters}
              title="필터 초기화"
            />
          </>
        }
        className="filters-container"
      />

      {/* 액션 버튼들 */}
      <div className="dashboard-actions">
        <Button
          icon="add"
          onClick={() => setShowItemTypeModal(true)}
          variant="primary"
        >
          새 항목
        </Button>
        <Button
          icon="refresh"
          onClick={loadItems}
          variant="ghost"
          disabled={loading}
          title="새로고침"
        />
      </div>

      {/* 에러 메시지 */}
      {error && (
        <div className="error-banner">
          <p>{error}</p>
        </div>
      )}

      {/* 아이템 목록 */}
      <div className="dashboard-content">
        <UnifiedItemList
          items={allItems}
          onEdit={handleEditItem}
          onDelete={handleDeleteItem}
          onShare={handleSharePassword}
          onToggleFavorite={handleToggleFavorite}
          loading={loading}
        />
      </div>

      {/* 모달들 */}
      <ItemTypeModal
        isOpen={showItemTypeModal}
        onClose={() => setShowItemTypeModal(false)}
        onSelectType={handleSelectItemType}
      />

      {renderForm()}

      {showShareModal && sharingPassword && (
        <PasswordShareModal
          password={sharingPassword}
          isOpen={showShareModal}
          onClose={() => {
            setShowShareModal(false);
            setSharingPassword(null);
          }}
        />
      )}

      <Suspense fallback={<LoadingFallback />}>
        {showSecurityRecommendations && (
          <SecurityRecommendations
            isOpen={showSecurityRecommendations}
            passwords={allItems.filter(item => item.itemType === 'password')}
            onPasswordUpdate={loadItems}
            onClose={() => setShowSecurityRecommendations(false)}
          />
        )}

        {showSettingsModal && (
          <SettingsModal
            isOpen={showSettingsModal}
            onClose={() => setShowSettingsModal(false)}
          />
        )}
      </Suspense>
    </div>
  );
}

export default DashboardNew;